import { Modal, Spin, Tooltip } from 'antd';
import React, { Suspense, useMemo, useState } from 'react';
import { FaRegTrashAlt } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { AiOutlineQuestionCircle } from 'react-icons/ai';
import { MdDeleteForever, MdOutlineOpenInNew } from 'react-icons/md';
import './productPreviewComponent.less';
import useScreenWidth from '../../../hooks/useScreenSize';

interface Props {
  productPath: string;
  label: string | JSX.Element;
  componentClassName?: string;
  labelClassName?: string;
  remarkTooltipText?: string;
  handleRemoveProduct?: () => void;
  showActionButtons?: boolean;
  showHint?: boolean;
}

const ProductPreviewComponent: React.FC<Props> = ({
  productPath,
  label,
  componentClassName,
  labelClassName,
  remarkTooltipText,
  handleRemoveProduct,
  showActionButtons = true,
  showHint = false,
}) => {
  const { shoppingCartTranslations: t } = useSelector(
    (data: RootState) => data.config.translations
  );
  const [openedModalUrl, setOpenedModalUrl] = useState<string | null>(null);
  // Phones: the product opens as a near full-screen sheet stacked above the
  // cart sheet — app bar (product name · ✕) over the page — instead of a
  // centred desktop window.
  const { isMobileWidth } = useScreenWidth();

  const productUrl = useMemo(() => {
    if (!productPath) {
      return null;
    }
    const baseUrl = window.location.origin;
    const searchParams = new URLSearchParams(productPath);
    searchParams.append('prvmd', '1');
    const url = new URL(baseUrl + productPath);
    url.search = searchParams.toString();
    return url.toString();
  }, [productPath]);

  return (
    <>
      <div
        className={`product-preview-wrapper ${!showActionButtons ? '' : 'clickable'} ${componentClassName ?? ''}`}
        onClick={() => showActionButtons && setOpenedModalUrl(productUrl)}
      >
        <Tooltip
          className="product-preview-tooltip"
          title={remarkTooltipText ? remarkTooltipText : label}
        >
          <span className={`cart-title ${labelClassName ?? ''}`}>{label}</span>
          {remarkTooltipText && showHint && (
            <AiOutlineQuestionCircle
              size={18}
              className={'hint additional-price-info'}
            />
          )}
        </Tooltip>
        {showActionButtons && (
          <>
            <span className="view-product-label-wrapper">
              <MdOutlineOpenInNew size={18} />
              {/* <span>{t.view}</span> */}
            </span>
            {handleRemoveProduct && (
              <span
                className="remove-product-label-wrapper"
                onClick={() => handleRemoveProduct()}
              >
                <FaRegTrashAlt size={18} color="red" />
              </span>
            )}
          </>
        )}
      </div>

      <Modal
        open={!!openedModalUrl}
        className="product-preview-modal"
        wrapClassName={
          isMobileWidth
            ? 'm-sheet m-sheet--stacked product-preview-sheet'
            : undefined
        }
        title={
          isMobileWidth ? (
            <span className="product-preview-sheet__title">{label}</span>
          ) : undefined
        }
        transitionName={isMobileWidth ? 'ant-move-down' : undefined}
        width={isMobileWidth ? '100%' : undefined}
        onCancel={() => {
          setOpenedModalUrl(null);
        }}
        closable
        maskClosable={isMobileWidth}
        footer={null}
        destroyOnClose
      >
        <Suspense
          fallback={
            <Spin className="center lazy-loader-spinner" size="large" />
          }
        >
          {openedModalUrl ? (
            <iframe key={openedModalUrl} src={openedModalUrl} />
          ) : null}
        </Suspense>
      </Modal>
    </>
  );
};

export default ProductPreviewComponent;
